import { useState, useEffect, useMemo, useContext } from 'react'
import styles from './MemberDetails.module.css'
import { AddMemberToGroupModal, EditMemberModal } from '../modals'
import AddMemberToProjectModal from '../modals/AddMemberToProjectModal'
import GroupsTableView from './GroupsTableView'
import ProjectsTableView from './ProjectsTableView'
import apiClient from '../../utils/api'
import GroupMembershipService from '../../services/GroupMembershipService'
import ProjectMembershipService from '../../services/ProjectMembershipService'
import { formatDisplayDate } from '../../utils/dateUtils'
import { DataRefreshContext } from '../ManageSection'

/**
 * MemberDetails - Component for displaying and managing member information
 * 
 * Follows SOLID principles:
 * - Single Responsibility: Only handles member details display and management
 * - Open/Closed: Extensible through props without modification
 * - Liskov Substitution: Can be replaced with other member detail implementations
 * - Interface Segregation: Focused interface for member operations
 * - Dependency Inversion: Depends on service abstractions
 */
export default function MemberDetails({
    member,
    onClose,
    apiToken,
    onSuccessMessage,
    onError,
    onMemberUpdate, // Optional callback to notify parent of updates
    onMemberRemoved // Optional callback to notify parent of removal
}) {
    const [currentMember, setCurrentMember] = useState(member)
    const [memberGroups, setMemberGroups] = useState([])
    const [availableGroups, setAvailableGroups] = useState([])
    const [showAddToGroupModal, setShowAddToGroupModal] = useState(false)
    const [showAddToProjectModal, setShowAddToProjectModal] = useState(false)
    const [showEditMemberModal, setShowEditMemberModal] = useState(false)
    const [loadingGroups, setLoadingGroups] = useState(false)
    const [memberProjects, setMemberProjects] = useState([])
    const [loadingProjects, setLoadingProjects] = useState(false)
    const [isRemoving, setIsRemoving] = useState(false)

    // Create service instances following Dependency Inversion Principle
    const groupMembershipService = useMemo(() => new GroupMembershipService(apiClient), [])
    const projectMembershipService = useMemo(() => new ProjectMembershipService(apiClient), [])

    // Get data refresh context to listen for global refresh events
    const dataRefreshContext = useContext(DataRefreshContext)
    const { refreshKey } = dataRefreshContext || {}

    // Sync currentMember state when member prop changes
    useEffect(() => {
        setCurrentMember(member)
    }, [member])

    // Load member data when component mounts or when member ID changes
    // This ensures fresh data is loaded every time a different member is selected
    useEffect(() => {
        if (currentMember?.id && apiToken) {
            console.log('[MemberDetails] Loading fresh data for member:', currentMember.id)
            loadMemberGroups(currentMember.id)
            loadMemberProjects(currentMember.id)
        }
    }, [currentMember?.id, apiToken])

    // Listen for global refresh events and reload member projects/groups
    // This ensures that when a project/group assignment happens in another tab or component,
    // this component refreshes to show the changes
    useEffect(() => {
        if (refreshKey > 0 && currentMember?.id && apiToken) {
            console.log('[MemberDetails] Detected global refresh event, reloading member data...')
            loadMemberGroups(currentMember.id)
            loadMemberProjects(currentMember.id)
        }
    }, [refreshKey])

    const loadMemberGroups = async (memberId) => {
        if (!apiToken || !memberId) return

        try {
            setLoadingGroups(true)
            apiClient.setAuthToken(apiToken)
            
            // Load all groups and filter for member groups
            const groupsResponse = await apiClient.getGroups()
            const allGroups = groupsResponse.groups || []
            
            setAvailableGroups(allGroups)
            
            // Filter groups that include this member
            const memberGroupsList = groupMembershipService.getMemberGroups(allGroups, memberId)
            setMemberGroups(memberGroupsList)
        } catch (err) {
            console.error('Failed to load member groups:', err)
            setMemberGroups([])
            setAvailableGroups([])
            if (onError) {
                onError('Failed to load member groups')
            }
        } finally {
            setLoadingGroups(false)
        }
    }

    const loadMemberProjects = async (memberId) => {
        if (!apiToken || !memberId) return

        try {
            setLoadingProjects(true)
            apiClient.setAuthToken(apiToken)
            
            const result = await projectMembershipService.getUserProjects(memberId)
            
            if (result.success) {
                const processedProjects = projectMembershipService.processProjectsWithAssociations(result.projects)
                setMemberProjects(processedProjects)
            } else {
                console.error('Failed to load member projects:', result.error)
                setMemberProjects([])
                if (onError) {
                    onError(result.error)
                }
            }
        } catch (err) {
            console.error('Failed to load member projects:', err)
            setMemberProjects([])
            if (onError) {
                onError('Failed to load member projects')
            }
        } finally {
            setLoadingProjects(false)
        }
    }

    const handleMemberAddedToGroup = async (group) => {
        // Refresh member groups to show the new group
        if (currentMember) {
            await loadMemberGroups(currentMember.id)
            // Notify parent to refresh member data for updated group count
            if (onMemberUpdate && apiToken) {
                try {
                    apiClient.setAuthToken(apiToken)
                    const response = await apiClient.getTeammate(currentMember.id)
                    if (response.teammate) {
                        onMemberUpdate(response.teammate)
                    }
                } catch (err) {
                    console.error('Failed to refresh member data:', err)
                }
            }
        }
        setShowAddToGroupModal(false)

        if (onSuccessMessage) {
            onSuccessMessage('Member added to group successfully')
        }
    }

    const handleRemoveMemberFromGroup = async (groupId, memberId) => {
        try {
            const result = await groupMembershipService.removeMemberFromGroup(groupId, memberId)
            
            if (result.success) {
                // Refresh member groups to reflect the removal
                if (currentMember) {
                    loadMemberGroups(currentMember.id)
                }
                
                if (onSuccessMessage) {
                    onSuccessMessage(result.message)
                }
            } else {
                console.error('Failed to remove member from group:', result.error)
                if (onError) {
                    onError(result.error)
                }
            }
        } catch (error) {
            console.error('Error removing member from group:', error)
            if (onError) {
                onError('Failed to remove member from group')
            }
        }
    }

    const handleMemberAddedToProject = (project) => {
        // Refresh member projects to show the new project
        if (currentMember) {
            loadMemberProjects(currentMember.id)
        }
        setShowAddToProjectModal(false)
        
        if (onSuccessMessage) {
            onSuccessMessage('Member added to project successfully')
        }
    }

    const handleRemoveDirectProjectAccess = async (projectId, memberId) => {
        try {
            const result = await projectMembershipService.removeUserFromProject(projectId, memberId)
            
            if (result.success) {
                // Refresh member projects to reflect the removal
                if (currentMember) {
                    loadMemberProjects(currentMember.id)
                }
                
                if (onSuccessMessage) {
                    onSuccessMessage(result.message)
                }
            } else {
                console.error('Failed to remove direct project access:', result.error)
                if (onError) {
                    onError(result.error)
                }
            }
        } catch (error) {
            console.error('Error removing direct project access:', error)
            if (onError) {
                onError('Failed to remove direct project access')
            }
        }
    }

    const handleMemberUpdated = (updatedMember) => {
        // Update local member state immediately for UI responsiveness
        setCurrentMember(updatedMember)
        
        // Close the modal
        setShowEditMemberModal(false)
        
        // Show success message
        if (onSuccessMessage) {
            onSuccessMessage('Member updated successfully')
        }
        
        // Notify parent component of the update if callback is provided
        if (onMemberUpdate) {
            onMemberUpdate(updatedMember)
        }
    }

    const handleRemoveMember = async () => {
        if (!currentMember) return
        
        // Confirm removal
        const confirmRemoval = window.confirm(
            `Are you sure you want to remove ${currentMember.full_name} from the workspace? This action cannot be undone.`
        )
        
        if (!confirmRemoval) return
        
        try {
            setIsRemoving(true)
            apiClient.setAuthToken(apiToken)
            
            const result = await apiClient.removeTeammate(currentMember.id)
            
            if (result.success) {
                if (onSuccessMessage) {
                    onSuccessMessage(result.message || 'Member removed from workspace successfully')
                }
                
                // Notify parent component of the removal
                if (onMemberRemoved) {
                    onMemberRemoved(currentMember)
                }
                
                // Close the details panel
                onClose()
            } else {
                const errorMsg = result.error || 'Failed to remove member from workspace'
                if (onError) {
                    onError(errorMsg)
                }
            }
        } catch (error) {
            console.error('Error removing member:', error)
            const errorMsg = error.response?.data?.error || 'Failed to remove member from workspace'
            if (onError) {
                onError(errorMsg)
            }
        } finally {
            setIsRemoving(false)
        }
    }

    const handleReactivateMember = async () => {
        if (!currentMember) return
        
        // Confirm reactivation
        const confirmReactivation = window.confirm(
            `Are you sure you want to reactivate ${currentMember.full_name} in the workspace?`
        )
        
        if (!confirmReactivation) return
        
        try {
            setIsRemoving(true)
            apiClient.setAuthToken(apiToken)
            
            // Update member status to active
            const result = await apiClient.updateWorkspaceMember(currentMember.id, { status: 'active' })
            
            if (result.success) {
                // Update local state
                const updatedMember = { ...currentMember, status: 'active' }
                setCurrentMember(updatedMember)
                
                if (onSuccessMessage) {
                    onSuccessMessage('Member reactivated successfully')
                }
                
                // Notify parent component of the update
                if (onMemberUpdate) {
                    onMemberUpdate(updatedMember)
                }
            } else {
                const errorMsg = result.error || 'Failed to reactivate member'
                if (onError) {
                    onError(errorMsg)
                }
            }
        } catch (error) {
            console.error('Error reactivating member:', error)
            const errorMsg = error.response?.data?.error || 'Failed to reactivate member'
            if (onError) {
                onError(errorMsg)
            }
        } finally {
            setIsRemoving(false)
        }
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'Never'
        return formatDisplayDate(dateString)
    }

    const getInitials = (firstName, lastName) => {
        const first = firstName?.charAt(0)?.toUpperCase() || ''
        const last = lastName?.charAt(0)?.toUpperCase() || ''
        return first + last
    }

    const capitalizeWord = (word) => {
        if (!word) return ''
        return word.charAt(0).toUpperCase() + word.slice(1)
    }

    const getStatusBadge = (status) => {
        const statusClass = {
            'active': styles.statusActive,
            'inactive': styles.statusInactive,
            'pending': styles.statusPending,
            'suspended': styles.statusSuspended
        }[status] || styles.statusActive

        const statusText = {
            'active': 'Active',
            'inactive': 'Inactive',
            'pending': 'Pending',
            'suspended': 'Suspended'
        }[status] || status

        return (
            <span className={`${styles.statusBadge} ${statusClass}`}>
                {statusText}
            </span>
        )
    }

    if (!currentMember) return null

    return (
        <div className={styles.memberDetails}>
            <div className={styles.detailsHeader}>
                <h2>{currentMember.full_name}</h2>
                <button 
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label="Close member details"
                >
                    ×
                </button>
            </div>
            
            <div className={styles.detailsContent}>
                <div className={styles.memberProfile}>
                    <div className={styles.memberAvatar}>
                        {currentMember.avatar_url ? (
                            <img src={currentMember.avatar_url} alt={currentMember.full_name} />
                        ) : (
                            <div className={styles.avatarPlaceholder}>
                                {getInitials(currentMember.first_name, currentMember.last_name)}
                            </div>
                        )}
                    </div>
                    <div className={styles.memberInfo}>
                        <h3>{currentMember.full_name}</h3>
                        <p className={styles.memberRole}>{capitalizeWord(currentMember.role)}</p>
                        <p className={styles.memberStatus}>{capitalizeWord(currentMember.status)}</p>
                    </div>
                </div>
                
                <div className={styles.detailSection}>
                    <h3>Contact Information</h3>
                    <div className={styles.detailGrid}>
                        <div className={styles.detailItem}>
                            <label>Email</label>
                            <span>{currentMember.email}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <label>Full Name</label>
                            <span>{currentMember.full_name}</span>
                        </div>
                    </div>
                </div>
                
                <div className={styles.detailSection}>
                    <h3>Workspace Details</h3>
                    <div className={styles.detailGrid}>
                        <div className={styles.detailItem}>
                            <label>Status</label>
                            <span>{getStatusBadge(currentMember.status)}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <label>Role</label>
                            <span>{capitalizeWord(currentMember.role)}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <label>Joined Workspace</label>
                            <span>{formatDate(currentMember.joined_at)}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <label>Last Sign In</label>
                            <span>{formatDate(currentMember.last_sign_in_at)}</span>
                        </div>
                        {currentMember.sign_in_count && (
                            <div className={styles.detailItem}>
                                <label>Sign In Count</label>
                                <span>{currentMember.sign_in_count}</span>
                            </div>
                        )}
                    </div>
                </div>
                
                <div className={styles.detailSection}>
                    <div className={styles.sectionHeader}>
                        <h3>Groups</h3>
                        <button 
                            className={styles.addGroupButton}
                            onClick={() => setShowAddToGroupModal(true)}
                        >
                            + Add to Group
                        </button>
                    </div>
                    <GroupsTableView
                        groups={memberGroups}
                        onRemoveGroup={handleRemoveMemberFromGroup}
                        loading={loadingGroups}
                        member={currentMember}
                    />
                </div>
                
                <div className={styles.detailSection}>
                    <div className={styles.sectionHeader}>
                        <h3>Projects</h3>
                        <button 
                            className={styles.addProjectButton}
                            onClick={() => setShowAddToProjectModal(true)}
                        >
                            + Add to Project
                        </button>
                    </div>
                    <ProjectsTableView
                        projects={memberProjects}
                        onRemoveDirectAccess={handleRemoveDirectProjectAccess}
                        loading={loadingProjects}
                        member={currentMember}
                    />
                </div>
                
                <div className={styles.detailSection}>
                    <h3>Actions</h3>
                    <div className={styles.detailActions}>
                        <button 
                            className={styles.primaryButton}
                            onClick={() => setShowEditMemberModal(true)}
                        >
                            Edit Member
                        </button>
                        {currentMember.status === 'inactive' ? (
                            <button 
                                className={styles.successButton}
                                onClick={handleReactivateMember}
                                disabled={isRemoving}
                            >
                                {isRemoving ? 'Reactivating...' : 'Reactivate Member'}
                            </button>
                        ) : (
                            <button 
                                className={styles.dangerButton}
                                onClick={handleRemoveMember}
                                disabled={isRemoving}
                            >
                                {isRemoving ? 'Removing...' : 'Remove from Workspace'}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Add Member to Group Modal */}
            <AddMemberToGroupModal
                isOpen={showAddToGroupModal}
                onClose={() => setShowAddToGroupModal(false)}
                onSave={handleMemberAddedToGroup}
                member={currentMember}
                availableGroups={availableGroups}
                memberGroups={memberGroups}
                apiToken={apiToken}
            />

            {/* Add Member to Project Modal */}
            <AddMemberToProjectModal
                isOpen={showAddToProjectModal}
                onClose={() => setShowAddToProjectModal(false)}
                onSave={handleMemberAddedToProject}
                member={currentMember}
                memberProjects={memberProjects}
                apiToken={apiToken}
            />

            {/* Edit Member Modal */}
            <EditMemberModal
                isOpen={showEditMemberModal}
                onClose={() => setShowEditMemberModal(false)}
                onSave={handleMemberUpdated}
                member={currentMember}
                apiToken={apiToken}
            />
        </div>
    )
}
